/**
 * Offline read cache (Offline-first, Phase 1).
 *
 * Persists the React Query cache to IndexedDB so staff on unstable clinic
 * connectivity can still SEE their data (today's appointments, patient list,
 * a patient's record) when the network drops — the app shell is already
 * offline-capable via the PWA service worker; this adds the DATA layer.
 *
 * Read-only by design: we only persist SUCCESSFUL queries and re-hydrate them
 * on startup. No offline writes / sync yet (that's a later, riskier phase) —
 * so there is zero data-conflict risk here.
 *
 * Privacy: the cache holds PHI, so it is wiped on logout (see auth-store
 * clearSession) and auto-expires after MAX_AGE. A version BUSTER drops the
 * cache across releases to avoid shape drift.
 *
 * Implemented on native IndexedDB + react-query's own dehydrate/hydrate — no
 * extra dependency.
 */
import { type QueryClient, dehydrate, hydrate } from "@tanstack/react-query";

const DB_NAME = "klyra-offline";
const STORE = "cache";
const KEY = "rq-cache";
const BUSTER = "v1"; // bump on a breaking cache-shape change to drop old caches
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h
const DEBOUNCE_MS = 2000;

interface Persisted {
	buster: string;
	ts: number;
	state: unknown;
}

let lastSync: number | null = null;
/** Epoch ms of the last successful cache persist (or hydrate), or null. */
export function getLastSync(): number | null {
	return lastSync;
}

function openDb(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const req = indexedDB.open(DB_NAME, 1);
		req.onupgradeneeded = () => {
			if (!req.result.objectStoreNames.contains(STORE))
				req.result.createObjectStore(STORE);
		};
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error);
	});
}

function idbGet<T>(key: string): Promise<T | undefined> {
	return openDb().then(
		(db) =>
			new Promise<T | undefined>((resolve, reject) => {
				const tx = db.transaction(STORE, "readonly");
				const req = tx.objectStore(STORE).get(key);
				req.onsuccess = () => resolve(req.result as T | undefined);
				req.onerror = () => reject(req.error);
			}),
	);
}

function idbSet(key: string, val: unknown): Promise<void> {
	return openDb().then(
		(db) =>
			new Promise<void>((resolve, reject) => {
				const tx = db.transaction(STORE, "readwrite");
				tx.objectStore(STORE).put(val, key);
				tx.oncomplete = () => resolve();
				tx.onerror = () => reject(tx.error);
			}),
	);
}

function idbDel(key: string): Promise<void> {
	return openDb().then(
		(db) =>
			new Promise<void>((resolve, reject) => {
				const tx = db.transaction(STORE, "readwrite");
				tx.objectStore(STORE).delete(key);
				tx.oncomplete = () => resolve();
				tx.onerror = () => reject(tx.error);
			}),
	);
}

function idbGetAllKeys(): Promise<IDBValidKey[]> {
	return openDb().then(
		(db) =>
			new Promise<IDBValidKey[]>((resolve, reject) => {
				const tx = db.transaction(STORE, "readonly");
				const req = tx.objectStore(STORE).getAllKeys();
				req.onsuccess = () => resolve(req.result);
				req.onerror = () => reject(req.error);
			}),
	);
}

// ===== Offline draft protection (form drafts that survive a network drop /
// reload). These are NEVER auto-submitted to the server — they only let the
// user RESTORE typed work and press save themselves. Stored in the same DB so
// the logout wipe covers them. PHI, so they auto-expire too. =====

const DRAFT_PREFIX = "draft:";
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface PersistedDraft<T> {
	ts: number;
	value: T;
}

/** Persist a form draft under a caller-chosen scope key (e.g. `visit:<id>`). */
export async function saveDraft<T>(key: string, value: T): Promise<void> {
	if (typeof indexedDB === "undefined") return;
	try {
		await idbSet(DRAFT_PREFIX + key, {
			ts: Date.now(),
			value,
		} satisfies PersistedDraft<T>);
	} catch {
		/* best-effort */
	}
}

/** Load a draft if present and not expired; expired drafts are dropped. */
export async function loadDraft<T>(
	key: string,
): Promise<{ value: T; ts: number } | null> {
	if (typeof indexedDB === "undefined") return null;
	try {
		const saved = await idbGet<PersistedDraft<T>>(DRAFT_PREFIX + key);
		if (!saved) return null;
		if (Date.now() - saved.ts > DRAFT_MAX_AGE_MS) {
			await idbDel(DRAFT_PREFIX + key);
			return null;
		}
		return { value: saved.value, ts: saved.ts };
	} catch {
		return null;
	}
}

/** Remove a single draft (call after a successful save, or on discard). */
export async function clearDraft(key: string): Promise<void> {
	try {
		await idbDel(DRAFT_PREFIX + key);
	} catch {
		/* best-effort */
	}
}

/** Wipe ALL drafts — called on logout so a logged-out device keeps no PHI. */
export async function clearAllDrafts(): Promise<void> {
	if (typeof indexedDB === "undefined") return;
	try {
		const keys = await idbGetAllKeys();
		await Promise.all(
			keys
				.filter((k) => typeof k === "string" && k.startsWith(DRAFT_PREFIX))
				.map((k) => idbDel(k as string)),
		);
	} catch {
		/* best-effort */
	}
}

/** Wipe the persisted cache — called on logout so PHI never lingers. */
export async function clearOfflineCache(): Promise<void> {
	lastSync = null;
	try {
		await idbDel(KEY);
	} catch {
		/* best-effort */
	}
}

let timer: ReturnType<typeof setTimeout> | undefined;

/**
 * Hydrate any fresh persisted cache on startup, then keep persisting the cache
 * (debounced) as queries succeed. Call once with the app's QueryClient.
 */
export function setupOfflineCache(qc: QueryClient): void {
	if (typeof indexedDB === "undefined") return;

	void idbGet<Persisted>(KEY)
		.then((saved) => {
			if (!saved) return;
			const fresh =
				saved.buster === BUSTER && Date.now() - saved.ts < MAX_AGE_MS;
			if (fresh) {
				try {
					hydrate(qc, saved.state);
					lastSync = saved.ts;
				} catch {
					void clearOfflineCache();
				}
			} else {
				void clearOfflineCache();
			}
		})
		.catch(() => {});

	qc.getQueryCache().subscribe(() => {
		clearTimeout(timer);
		timer = setTimeout(() => {
			try {
				const state = dehydrate(qc, {
					shouldDehydrateQuery: (q) => q.state.status === "success",
				});
				const ts = Date.now();
				void idbSet(KEY, { buster: BUSTER, ts, state } satisfies Persisted)
					.then(() => {
						lastSync = ts;
					})
					.catch(() => {});
			} catch {
				/* ignore serialization issues */
			}
		}, DEBOUNCE_MS);
	});
}
